import { Palette, Check } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/lib/AuthContext";

const DARK = [
  { id: "gold", label: "Gold", bg: "#070C09", accent: "#D4AF37" },
  { id: "ocean", label: "Ocean", bg: "#060B12", accent: "#38BDF8" },
  { id: "forest", label: "Forest", bg: "#04110A", accent: "#9BCB5B" },
  { id: "slate", label: "Slate", bg: "#0A0B0D", accent: "#A5B4CB" },
  { id: "crimson", label: "Crimson", bg: "#0E0607", accent: "#E0455B" },
  { id: "amethyst", label: "Amethyst", bg: "#0A0710", accent: "#B794F6" },
];

const LIGHT = [
  { id: "white", label: "White", bg: "#FFFFFF", accent: "#A17C16" },
  { id: "sky", label: "Sky", bg: "#F0F7FF", accent: "#0284C7" },
  { id: "mint", label: "Mint", bg: "#EEF9F3", accent: "#059669" },
  { id: "blush", label: "Blush", bg: "#FDF2F4", accent: "#DB2777" },
  { id: "lavender", label: "Lavender", bg: "#F5F3FF", accent: "#7C3AED" },
];

const Swatch = ({ t, active, onPick }) => (
  <button
    onClick={() => onPick(t.id)}
    className={`group flex flex-col items-center gap-1 p-1.5 border transition-colors ${active ? "border-bt-gold" : "border-transparent hover:border-bt-border-focus"}`}
    data-testid={`theme-swatch-${t.id}`}
  >
    <span className="relative w-9 h-9 rounded-full border border-black/15 overflow-hidden" style={{ background: t.bg }}>
      <span className="absolute inset-y-0 right-0 w-1/2" style={{ background: t.accent }} />
      {active && <Check className="absolute inset-0 m-auto w-4 h-4 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]" strokeWidth={3} />}
    </span>
    <span className="font-body text-[10px] text-bt-text-secondary group-hover:text-bt-text-primary">{t.label}</span>
  </button>
);

const Group = ({ title, list, current, onPick }) => (
  <>
    <p className="font-mono text-[10px] uppercase tracking-wider text-bt-text-tertiary mb-2">{title}</p>
    <div className="grid grid-cols-3 gap-2 mb-3 last:mb-0">
      {list.map(t => <Swatch key={t.id} t={t} active={current === t.id} onPick={onPick} />)}
    </div>
  </>
);

export const ThemePicker = () => {
  const { user, setTheme } = useAuth();
  const current = user?.theme || "sky";
  const pick = (id) => setTheme(id).catch(() => toast.error("Couldn't save theme"));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="shrink-0 flex items-center justify-center w-8 h-8 border border-bt-border-subtle bg-bt-surface text-bt-text-secondary hover:border-bt-border-focus hover:text-bt-gold transition-colors"
          title="Color theme"
          data-testid="theme-picker-btn"
        >
          <Palette className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-3 bg-bt-surface border-bt-border-subtle rounded-none" data-testid="theme-picker-panel">
        <Group title="Dark" list={DARK} current={current} onPick={pick} />
        <Group title="Light" list={LIGHT} current={current} onPick={pick} />
      </PopoverContent>
    </Popover>
  );
};
